import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "react-router-dom";
import {
  Button as MuiButton,
  CircularProgress as MuiCircularProgress,
  Grid as MuiGrid,
  MenuItem as MuiMenuItem,
  TextField as MuiTextField,
  Typography as MuiTypography,
} from "@mui/material";
import { DataGridPro as MuiDataGrid } from "@mui/x-data-grid-pro";
import { CloudUpload as MuiCloudUploadIcon } from "@mui/icons-material";
import { LoadingGridOverlay, NoRowsGridOverlay, useAlerts } from "common";
import { importProspectsPageStyles } from "app/components/prospect/ImportProspectsPageStyles";
import ConfirmationDialog from "app/shared/UI/Dialog/ConfirmationDialog";
import {
  GET_IMPORTS_URL_BASE,
  GET_STUDIES_URL_BASE,
  ImportStatuses,
  LOADER_TYPES,
  SourceTypeOptions,
} from "app/shared/constants";
import {
  addReverseIndexToElements,
  mapStudyToDisplayed,
} from "app/shared/utils";
import { tableStyles } from "app/shared/UI/sharedStyles";
import { defaultTableColumnHeaders } from "./ImportProspectsTableConfig";
import ErrorDialog from "app/shared/UI/Dialog/ErrorDialog";

const defaultPageNumber = 0;

const ImportProspectsPageContent = (props) => {
  const { intervalSetter } = props;
  const { studyId } = useParams();

  const location = useLocation();
  const { state } = location;

  const { t } = useTranslation();
  const { setAlert } = useAlerts();
  const [openASConfirmation, setOpenASConfirmation] = useState(false);
  const [selectedFiles, setSelectdFiles] = useState([]);
  const [studyImports, setStudyImports] = useState([]);
  const refStudyImports = useRef(studyImports);
  const [study, setStudy] = useState(null);
  const [importSource, setImportSource] = useState("STARR_HBE");
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedImportRequest, setSelectedImportRequest] = useState(-1);
  const replaceObsolete = false;
  const [buttonLoader, setButtonLoader] = useState(false);
  const [importIds, setImportIds] = useState([]);
  const [importFileNames, setImportFileNames] = useState([]);
  const [importFileDuplicate, setImportFileDuplicate] = useState(false);
  const [dialogData, setDialogData] = useState({
    index: null,
    report: null,
  });

  const initialLoading = {
    importProspectsRecentActivities: true,
  };

  const [isLoading, setIsLoading] = useState(initialLoading);
  // Pseudo-reducer to keep loading states updated
  const setLoader = (type, isSet) => {
    setIsLoading((prev) => ({
      ...prev,
      [type]: isSet,
    }));
  };

  const maxUploadFileSizeInMb = 50;

  const postImportProspects = async () => {
    try {
      // call POST API to import chosen file
      const apiHeader = {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        // import a file of 50MB takes 80s, increase the timeout to accommodate (defined in .env)
        timeout: process.env.REACT_APP_IMPORT_UPLOAD_TIMEOUT,
      };

      const formData = new FormData();
      formData.append("file", selectedFiles[0]);

      const postURL =
        GET_IMPORTS_URL_BASE +
        "/" +
        importSource +
        "/protocol/" +
        study.protocolNumber +
        "?obsolete-prospects=" +
        replaceObsolete;

      const response = await axios.post(postURL, formData, apiHeader);

      // reset import section after import successfully
      setSelectdFiles([]);
      // return import_reqest, should be added to the top of the Recent Activity list
      const newstudyImports = [...studyImports];
      newstudyImports.unshift(response.data);
      setStudyImports(newstudyImports);
      refStudyImports.current = newstudyImports;

      const ids = [...importIds, response.data.id];
      setImportIds(ids);
      const fileNames = [...importFileNames, response.data.filename];
      setImportFileNames(fileNames);

      // set a timer to retrieve the status of the import_request everty X seconds(defined in .env)
      // till the status become "Finished"
    } catch (err) {
      // set error.message in AlertContext
      setAlert("error", err.response.data.message);
    } finally {
      setButtonLoader(false);
    }
  };

  useEffect(() => {
    (async () => {
      if (studyId) {
        // fetch study by id
        try {
          const response = await axios.get(
            `${GET_STUDIES_URL_BASE}/${studyId}`
          );

          setStudy(mapStudyToDisplayed(response.data)); // save this for comparison at edit save
        } catch (err) {
          setAlert("error", err.message); // set error.message in AlertContext
        }
      }
    })();
  }, [setAlert, studyId]);

  useEffect(() => {
    if (study) {
      setLoader(LOADER_TYPES.IMPORT_PROSPECTS_RECENT_ACTIVITIES, true);

      (async () => {
        try {
          // fetch the import recent activities by protocolNumber
          const response = await axios.get(
            `${GET_IMPORTS_URL_BASE}?protocolNumber=${study.protocolNumber}`,
            {
              timeout: process.env.REACT_APP_IMPORT_HISTORY_TIMEOUT,
            }
          );

          setStudyImports(response.data);
          refStudyImports.current = response.data;

          const fileNames = response.data?.map((row) => row.filename);
          setImportFileNames(fileNames);
        } catch (err) {
          // set error.message in AlertContext
          setAlert("error", err.message);
        } finally {
          setLoader(LOADER_TYPES.IMPORT_PROSPECTS_RECENT_ACTIVITIES, false);
        }
      })();
    }
  }, [study, setAlert]);

  const onDrop = useCallback((acceptedFiles) => {
    setSelectdFiles(acceptedFiles);
  }, []);

  const onDropRejected = useCallback(
    (fileRejections) => {
      setAlert(
        "error",
        fileRejections[0].errors
          .map((e) => e.code)
          .map((c) => {
            switch (c) {
              case "file-invalid-type":
                return t("ImportProspectsPage.importFileInvalidTypeMessage", {
                  validFileType: ".csv",
                });
              case "file-too-large":
                return t("ImportProspectsPage.importFileTooLargeMessage", {
                  maxFileSize: maxUploadFileSizeInMb,
                });
              default:
                return t("ImportProspectsPage.importFileGenericError");
            }
          })
          .join(", ")
      );
    },
    [t, setAlert]
  );

  const { getRootProps, getInputProps } = useDropzone({
    accept: ".csv",
    maxSize: maxUploadFileSizeInMb * 1024 * 1024,
    multiple: false,
    onDrop,
    onDropRejected,
  });

  const acceptedFilesItems = selectedFiles.map((file, index) => (
    <span key={index}>
      {file.path} - {file.size} bytes
    </span>
  ));

  const closeCancelASConfirmation = (okOnClose) => {
    setOpenASConfirmation(false);
    setImportFileDuplicate(false);
    if (okOnClose) {
      setButtonLoader(true);
      // click confirmation OK button, proceed to Import
      postImportProspects();
    }
  };

  useEffect(() => {
    var timerVar = [];
    if (importIds.length) {
      //timerId is for associating every import with a unique id which will help us to clear that particular interval
      let timerId = {};
      let nStudyImports = [];
      timerVar = importIds.map((row, idx) => {
        //this is to update nStudyImports with studyImports at every mapping
        nStudyImports = [...refStudyImports.current];

        return setInterval(async () => {
          // call BE API to retrieve the import_request by id
          //this is to update nStudyImports with nStudyImports at every interval call to update multiple rows data simultaniously
          nStudyImports = [...nStudyImports];

          try {
            timerId[row] = timerVar[idx];
            const result = await axios.get(`${GET_IMPORTS_URL_BASE}/${row}`);

            const dataIndex = refStudyImports.current.findIndex(
              (studyImpObj) => studyImpObj.id === row
            );

            nStudyImports[dataIndex] = result.data;
            setStudyImports(nStudyImports);

            if (
              result.data.status === ImportStatuses.FINISHED ||
              result.data.status === ImportStatuses.FINISHED_WITH_ERROR ||
              result.data.status === ImportStatuses.FAILED
            ) {
              clearInterval(timerId[row]);
              //this is to manage every import file ids.
              importIds.shift(row);
              //this is to delete completed timerId from the set.
              delete timerId[row];
            }
          } catch (err) {
            // set error.message in AlertContext
            setAlert("error", err.message);
            clearInterval(timerId.row);
            importIds.shift(row);
            delete timerId[row];
          }
        }, process.env.REACT_APP_IMPORT_PROSPECTS_REFRESH_RATE);
      });

      timerVar.length > 0 && timerVar.map((id) => intervalSetter(id));
    }

    return () => {
      timerVar.length > 0 && timerVar.map((id) => clearInterval(id));
    };
  }, [importIds, intervalSetter, setAlert]);

  const handleClickReset = () => {
    setSelectdFiles([]);
  };

  const handleClickImport = () => {
    if (importFileNames.includes(selectedFiles[0].name))
      setImportFileDuplicate(true);
    setOpenASConfirmation(true);
  };

  const displayImportFailureDialog = (index, rowReport) => {
    setOpenDialog(true);
    setSelectedImportRequest(index);
    setDialogData({
      index: index,
      report: rowReport,
    });
  };

  const closeImportFailureDialog = () => {
    setOpenDialog(false);
    setSelectedImportRequest(-1);
    setDialogData({
      index: null,
      report: null,
    });
  };

  const NoStudyOverlay = () => (
    <NoRowsGridOverlay message={t("common.emptyTableRow")} />
  );

  const errorSummaryContent = {
    primaryContent: "row",
    secondaryContent: "message",
  };

  return study || (state && state.study) ? (
    <MuiGrid container>
      <ConfirmationDialog
        open={openASConfirmation}
        onClose={closeCancelASConfirmation}
        title={t("ImportProspectsPage.confirmation_title")}
        message={
          importFileDuplicate
            ? t("ImportProspectsPage.duplicateImportMessage")
            : t("ImportProspectsPage.confirmation_message")
        }
        okLabel={t("ImportProspectsPage.confirmation_OK")}
        cancelLabel={t("ImportProspectsPage.confirmation_cancel")}
      />

      {openDialog && dialogData.index === selectedImportRequest && (
        <ErrorDialog
          onClose={closeImportFailureDialog}
          open={openDialog}
          errorsList={dialogData.report}
          title={t(`ImportProspectsPage.importFailureSummaryTitle`)}
          primarySubHeading={t(`ImportProspectsPage.importFailureLineNumber`)}
          primaryContent={errorSummaryContent.primaryContent}
          secondaryContent={errorSummaryContent.secondaryContent}
          isLoading={isLoading}
        />
      )}
      <MuiGrid container size={12}>
        <MuiGrid size={2} sx={importProspectsPageStyles.gridContainer}>
          <MuiTypography>
            {t(`formLabel.irb`)}{" "}
            <MuiTypography variant="subtitle1" display="inline">
              {study ? study.irbNumber : state.study.irbNumber}
            </MuiTypography>
          </MuiTypography>
          <MuiTypography>
            {t(`formLabel.protocolNumber`)}: &nbsp;
            <MuiTypography variant="subtitle1" display="inline">
              {study ? study.protocolNumber : state.study.protocolNumber}
            </MuiTypography>
          </MuiTypography>
        </MuiGrid>
        <MuiGrid size={10} sx={importProspectsPageStyles.gridContainer}>
          <MuiTypography>
            {`${t("formLabel.studyNickname")}: `}
            <MuiTypography variant="subtitle1" display="inline">
              {study ? study.nickname : state.study.nickname}
            </MuiTypography>
          </MuiTypography>
        </MuiGrid>
        <MuiGrid container sx={importProspectsPageStyles.importSection}>
          <MuiGrid size={12}>
            <MuiTextField
              sx={importProspectsPageStyles.formTextField}
              select
              label={t(`ImportProspectsPage.sourceTypeLabel`)}
              placeholder={t(`ImportProspectsPage.sourceTypeLabel`)}
              value={importSource}
              onChange={(evt) => setImportSource(evt.target.value)}
              size="small"
              variant="outlined"
              margin="dense"
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
              }}
            >
              {SourceTypeOptions.map((item) => (
                <MuiMenuItem key={item} value={item}>
                  {`${t("ProspectSourceType." + item)}`}
                </MuiMenuItem>
              ))}
            </MuiTextField>
          </MuiGrid>
          <div
            style={importProspectsPageStyles.dragDropContainer}
            {...getRootProps()}
          >
            <input {...getInputProps()} />
            <MuiCloudUploadIcon fontSize="small" />
            <p>{t(`ImportProspectsPage.dragNDropFile`)}</p>
          </div>
          <MuiGrid container>
            {selectedFiles && selectedFiles.length > 0 && (
              <>
                <MuiGrid
                  size={12}
                  sx={importProspectsPageStyles.textAlignCenter}
                >
                  <MuiTypography variant="subtitle1">
                    {t(`ImportProspectsPage.fileToUpload`)}&nbsp;
                    {acceptedFilesItems}
                  </MuiTypography>
                </MuiGrid>
                <MuiGrid size={12}>
                  <div sx={importProspectsPageStyles.importActionContainer}>
                    <div sx={importProspectsPageStyles.wrapper}>
                      <MuiButton
                        variant="contained"
                        color="primary"
                        disabled={buttonLoader}
                        onClick={handleClickImport}
                      >
                        {t(`ImportProspectsPage.importButtonLabel`)}
                      </MuiButton>
                      {buttonLoader && (
                        <MuiCircularProgress
                          size={24}
                          sx={importProspectsPageStyles.buttonProgress}
                        />
                      )}
                    </div>
                    <MuiButton onClick={handleClickReset}>
                      {t(`ImportProspectsPage.resetButtonLabel`)}
                    </MuiButton>
                  </div>
                </MuiGrid>
              </>
            )}
          </MuiGrid>
        </MuiGrid>
      </MuiGrid>
      <MuiGrid container size={12}>
        <MuiGrid size={12} sx={importProspectsPageStyles.titleContainer}>
          <MuiTypography variant="h2">
            {t(`ImportProspectsPage.importRecentActivities`)}
          </MuiTypography>
        </MuiGrid>
        <MuiGrid size={12} sx={importProspectsPageStyles.tableScroll}>
          <MuiDataGrid
            slots={{
              loadingOverlay: LoadingGridOverlay,
              noRowsOverlay: NoStudyOverlay,
            }}
            rows={
              studyImports &&
              addReverseIndexToElements(
                studyImports,
                studyImports.length,
                defaultPageNumber,
                studyImports.length
              )
            }
            getRowId={(row) => row.id}
            columns={defaultTableColumnHeaders(t, displayImportFailureDialog)}
            autoHeight
            disableColumnMenu
            disableColumnResize
            disableColumnReorder
            disableMultipleColumnsSorting
            disableRowSelectionOnClick
            loading={isLoading[LOADER_TYPES.IMPORT_PROSPECTS_RECENT_ACTIVITIES]}
            getRowClassName={(params) =>
              params.indexRelativeToCurrentPage % 2 === 0
                ? "Mui-even"
                : "Mui-odd"
            }
            sx={tableStyles}
            columnHeaderHeight={70}
            getRowHeight={() => "auto"}
          />
        </MuiGrid>
      </MuiGrid>
    </MuiGrid>
  ) : null;
};

export default ImportProspectsPageContent;
